import { compareIso, normalizeIso, type IsoDate, type IsoDateRange } from './isoDate';

/**
 * WHAT A CLICK MEANS IN A RANGE — the platform calendar spec's *Selecting dates*, as a
 * pure function of the range being edited and the end that is active.
 *
 * The range field keeps one of these as its DRAFT while the calendar is open and only
 * publishes it once it is settled (see `isSettled`): a half-picked range never reaches the
 * page.
 */

export type Bound = 'start' | 'end';

export interface RangeDraft {
  start: IsoDate | null;
  end: IsoDate | null;
  /** A start and deliberately no end (an open-ended period). `end` is then `null`. */
  openEnded: boolean;
}

export const EMPTY_DRAFT: RangeDraft = { start: null, end: null, openEnded: false };

export function toDraft(range: IsoDateRange, openEnded = false): RangeDraft {
  const start = normalizeIso(range.start);
  const end = normalizeIso(range.end);
  return { start, end: openEnded ? null : end, openEnded: openEnded && start !== null };
}

export function toRange(draft: RangeDraft): IsoDateRange {
  return { start: draft.start, end: draft.openEnded ? null : draft.end };
}

export function draftsEqual(a: RangeDraft, b: RangeDraft): boolean {
  return a.start === b.start && a.end === b.end && a.openEnded === b.openEnded;
}

/**
 * Whether the range may be published: both ends, a start with an open end, or nothing at
 * all (a cleared field). Anything else is on its way somewhere.
 */
export function isSettled(draft: RangeDraft): boolean {
  if (draft.openEnded) return draft.start !== null;
  return (draft.start === null) === (draft.end === null);
}

export interface PickResult {
  draft: RangeDraft;
  /** The end the next click sets. */
  active: Bound;
  /** The click finished the range: publish it and close. */
  done: boolean;
}

/**
 * One picked day.
 *
 * - **Start active:** the day is the start, and the end is next. An end it would pass is
 *   dropped (the range must be re-ended), one it does not pass is kept. With an open end
 *   the range is finished.
 * - **End active, a day before the start:** that day becomes the new start; the end stays
 *   empty and is still next.
 * - **End active otherwise:** the day is the end (the start's own day makes a one-day
 *   range), which finishes the range — or, with no start yet, the start is next.
 *
 * Picking an end ends an open end.
 */
export function pickRangeDay(draft: RangeDraft, active: Bound, day: IsoDate): PickResult {
  if (active === 'start') {
    const keepEnd = draft.end !== null && compareIso(day, draft.end) <= 0 ? draft.end : null;
    return {
      draft: { start: day, end: keepEnd, openEnded: draft.openEnded },
      active: 'end',
      done: draft.openEnded,
    };
  }
  if (draft.start !== null && compareIso(day, draft.start) < 0) {
    return { draft: { start: day, end: null, openEnded: false }, active: 'end', done: false };
  }
  const next: RangeDraft = { start: draft.start, end: day, openEnded: false };
  return next.start === null
    ? { draft: next, active: 'start', done: false }
    : { draft: next, active: 'end', done: true };
}

/**
 * "Open end" chosen: the range keeps its start and gives up its end. With a start it is
 * finished; without one the start is next, and picking it finishes the range.
 */
export function openRangeEnd(draft: RangeDraft): PickResult {
  const next: RangeDraft = { start: draft.start, end: null, openEnded: true };
  return draft.start === null
    ? { draft: next, active: 'start', done: false }
    : { draft: next, active: 'end', done: true };
}

/** A day the pointer or keyboard is on previews the range up to it — forward of the start only. */
export function previewsTo(draft: RangeDraft, active: Bound, day: IsoDate | null): boolean {
  return (
    active === 'end' && draft.start !== null && day !== null && compareIso(day, draft.start) > 0
  );
}
